/*
 * See the file LICENSE for the full license governing this code.
 */
import * as React from 'react';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CircleIcon from '@mui/icons-material/Circle';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CircularProgress from '@mui/material/CircularProgress';

const SmallCircularProgress = React.forwardRef(function SmallCircularProgress(
  props,
  ref
) {
  return <CircularProgress ref={ref} size={20} {...props} />;
});

const AnimatedHourglassIcon = React.forwardRef(function AnimatedHourglassIcon(
  props,
  ref
) {
  const { sx, ...rest } = props;
  return (
    <HourglassBottomIcon
      ref={ref}
      sx={{
        animation: 'rotate 3s steps(2, jump-none) infinite',
        '@keyframes rotate': {
          to: {
            transform: 'rotate(180deg)',
          },
        },
        ...sx,
      }}
      {...rest}
    />
  );
});

function StateIcon({ state, customTitle }) {
  const componentWithColor = () => {
    if (state === 'on' || state === 'created') {
      return {
        Component: CheckCircleIcon,
        color: 'success.main',
        dataCy: 'success-icon',
        title: 'Server is running',
      };
    }
    if (state === 'off') {
      return {
        Component: CircleIcon,
        dataCy: 'off-icon',
        color: 'grey.500',
        title: 'Server is turned off',
      };
    }
    if (state === 'error') {
      return {
        Component: ErrorIcon,
        color: 'error.main',
        dataCy: 'error-icon',
        title: 'An error occured. Please contact support',
      };
    }
    if (state === 'changing') {
      return {
        Component: AnimatedHourglassIcon,
        color: 'primary.main',
        dataCy: 'wait-icon',
        title: 'Server is being updated, please wait',
      };
    }
    if (state === 'removing') {
      return {
        Component: DeleteForeverIcon,
        color: 'primary.main',
        dataCy: 'remove-icon',
        title: 'Server is being deleted',
      };
    }
    return {
      Component: SmallCircularProgress,
      color: 'primary.main',
      title: 'Receiving server state',
      dataCy: 'fetch-icon',
    };
  };
  const { Component, color, title, dataCy } = componentWithColor();
  if (Component && color) {
    return (
      <Tooltip title={customTitle || title}>
        <Component data-cy={dataCy} sx={{ color, m: 1 }} />
      </Tooltip>
    );
  }
  return null;
}

export default StateIcon;
